import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { RotateCcw, X } from "lucide-react";
import { usePageMeta } from "../lib/usePageMeta";
import { JourneyProvider, useJourney } from "../components/intention/JourneyContext";
import { JourneyLanding } from "../components/intention/JourneyLanding";
import { JourneyParticles, StageProgress } from "../components/intention/StageChrome";
import { WorldStage } from "../components/intention/stages/WorldStage";
import { PullStage } from "../components/intention/stages/PullStage";
import { TradeoffStage } from "../components/intention/stages/TradeoffStage";
import { PeopleStage } from "../components/intention/stages/PeopleStage";
import { UnfinishedStage } from "../components/intention/stages/UnfinishedStage";
import { ConstellationStage } from "../components/intention/stages/ConstellationStage";
import { UnlockedStage } from "../components/intention/stages/UnlockedStage";
import "../components/intention/intention.css";

const STAGES = { 0: JourneyLanding, 1: WorldStage, 2: PullStage, 3: TradeoffStage, 4: PeopleStage, 5: UnfinishedStage, 6: ConstellationStage, 7: UnlockedStage };

const JourneyCanvas = () => {
  const { journey, reset } = useJourney();
  const canvasRef = useRef(null);
  const Stage = STAGES[journey.stage] || JourneyLanding;

  useEffect(() => {
    document.documentElement.classList.toggle("intention-journey-running", journey.stage > 0);
    return () => document.documentElement.classList.remove("intention-journey-running");
  }, [journey.stage]);

  const moveLight = (event) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    canvasRef.current.style.setProperty("--cursor-x", `${event.clientX - rect.left}px`);
    canvasRef.current.style.setProperty("--cursor-y", `${event.clientY - rect.top}px`);
  };

  return (
    <main ref={canvasRef} className={`intention-canvas stage-${journey.stage}`} onPointerMove={moveLight} data-testid="intention-journey-page">
      <JourneyParticles />
      <div className="intention-cursor-light" aria-hidden="true" />
      <header className="intention-chrome"><Link to="/" aria-label="Exit to HUMN home" data-testid="intention-exit-logo"><img src="/brand/humn-wordmark.png" alt="HUMN" /></Link>{journey.stage > 0 && <div><button type="button" onClick={reset} data-testid="intention-reset-button"><RotateCcw size={14} /><span>Start over</span></button><Link to="/" aria-label="Exit journey" data-testid="intention-exit-button"><X size={17} /></Link></div>}</header>
      <AnimatePresence initial={false}><motion.div key={journey.stage} className={`intention-stage-transition ${journey.stage === 7 ? "is-scrollable" : ""}`} initial={{ opacity: 0, scale: .96, filter: "blur(12px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)", zIndex: 5 }} exit={{ position: "absolute", inset: 0, opacity: 0, scale: 1.04, filter: "blur(14px)", pointerEvents: "none", zIndex: 1 }} transition={{ duration: .68, ease: [.22, 1, .36, 1] }}><Stage /></motion.div></AnimatePresence>
      {journey.stage > 0 && journey.stage < 7 && <StageProgress stage={journey.stage} />}
    </main>
  );
};

export default function IntentionJourneyPage() {
  usePageMeta("Discover Your Intention — HUMN", "Seven immersive moments. One HumnAi-generated direction. Discover what pulls you, then make it happen with HUMN.");
  useEffect(() => {
    document.documentElement.classList.add("intention-route-active");
    return () => document.documentElement.classList.remove("intention-route-active");
  }, []);
  return <JourneyProvider><JourneyCanvas /></JourneyProvider>;
}